import { useCallback, useMemo, useState } from 'react';

import { DropdownSelector } from '@/components/generic/DropdownSelector';
import { isWarranty, mapPriceBookItemToOrderItem } from '@/components/modals/SubmitOrder/constants';
import { useOrderContext, useOrderDispatch } from '@/components/modals/SubmitOrder/helpers';
import { PriceBookEntryVm } from '@/types';
import { formatPriceWithCurrency } from '@/utils/format-price';

import { OrderBlock } from '../../../Common/OrderBlock';
import { OrderBlockWrapper } from '../../../Common/OrderBlockWrapper';

import { calcWarrantiesTotal, calcWarrantyTotal, getWarrantyName } from './helpers';
import { Props } from './types';

import styles from './Warranty.module.scss';

export const Warranty = ({ allPriceBookEntries }: Props): JSX.Element | null => {
  const state = useOrderContext();
  const dispatch = useOrderDispatch();
  const [selectedWarranty, setSelectedWarranty] = useState<PriceBookEntryVm | null | undefined>(
    undefined,
  );
  const [expanded, setExpanded] = useState<boolean>(false);
  const warranties = useMemo(
    () =>
      allPriceBookEntries
        .filter(item => isWarranty(item) && item.isProductAvailable && !item.product.isPreSelected)
        .sort((first, second) => first.product.name.localeCompare(second.product.name)),
    [allPriceBookEntries],
  );
  const preSelectedWarranties = useMemo(
    () =>
      allPriceBookEntries.filter(
        item => isWarranty(item) && item.isProductAvailable && item.product.isPreSelected,
      ),
    [allPriceBookEntries],
  );

  const getDropDownText = () => {
    if (!selectedWarranty) {
      return 'Select an option';
    }

    return getWarrantyName(selectedWarranty);
  };

  const onTypeSelect = useCallback(
    (priceBookItem: PriceBookEntryVm | null) => {
      setSelectedWarranty(priceBookItem);

      const orderItems = [...state.visibleOrderItems];

      if (!priceBookItem) {
        dispatch({
          type: 'UPDATE_VISIBLE_ORDER_ITEMS',
          payload: orderItems.filter(
            ({ priceBookEntry }) =>
              !isWarranty(priceBookEntry) && !priceBookEntry.product.isPreSelected,
          ),
        });

        return;
      }

      const warrantyIndex = orderItems.findIndex(
        ({ priceBookEntry }) => isWarranty(priceBookEntry) && !priceBookEntry.product.isPreSelected,
      );

      const orderItem = {
        priceBookEntry: priceBookItem,
        orderItem: mapPriceBookItemToOrderItem(priceBookItem),
        proposalItem: null,
      };

      if (warrantyIndex < 0) {
        orderItems.push(orderItem);
      } else {
        orderItems[warrantyIndex] = orderItem;
      }

      dispatch({
        type: 'UPDATE_VISIBLE_ORDER_ITEMS',
        payload: orderItems,
      });
    },
    [dispatch, allPriceBookEntries, state.visibleOrderItems],
  );

  if (!warranties.length || !state.isAccessProgram) {
    return null;
  }

  const getWarrantiesToPay = () => {
    if (selectedWarranty) {
      return [selectedWarranty, ...preSelectedWarranties];
    }

    return preSelectedWarranties;
  };

  return (
    <>
      <OrderBlock
        hideValue={state.isPriceHidden}
        title="Warranty"
        value={calcWarrantiesTotal(getWarrantiesToPay(), state.order.roofSize)}
      >
        <br />
        <OrderBlockWrapper color="#fafafa">
          <div className={styles.section_wrapper}>
            <div className={styles.label_wrapper}>
              <div className={styles.label}>Select Type:</div>
              <span className={styles.description}>
                Enter the warranty that you require for this order
              </span>
            </div>

            <div className={styles.value_wrapper}>
              <DropdownSelector
                isOpen={expanded}
                name="warranty"
                onSelect={onTypeSelect}
                onToggle={() => setExpanded(expanded => !expanded)}
                optionNameAccessor="product.portalName"
                options={warranties}
                selectedOption={selectedWarranty}
                value={getDropDownText()}
              />
            </div>
          </div>
        </OrderBlockWrapper>
        {preSelectedWarranties.length > 0 && (
          <>
            <br />
            {preSelectedWarranties.map(item => (
              <div key={item.productId}>
                <OrderBlockWrapper color="#FAFAFA">
                  <div className={styles.preselected_product_wrapper}>
                    <div className={styles.preselected_product}>{getWarrantyName(item)}</div>
                    {!state.isPriceHidden && (
                      <div className={styles.preselected_product}>
                        {formatPriceWithCurrency(calcWarrantyTotal(item, state.order.roofSize))}
                      </div>
                    )}
                  </div>
                </OrderBlockWrapper>
                <br />
              </div>
            ))}
          </>
        )}
      </OrderBlock>
      <br />
    </>
  );
};
